import { CaretLeft, Funnel, X } from '@phosphor-icons/react'
import { FocusSkillStar } from 'components/focus-skill-star'
import { AnimatePresence, motion } from 'framer-motion'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { Avatar } from 'src/design-system'
import { CheckinStatusLabel } from 'components/checkin-status-label'
import type { CheckinStatus } from 'components/checkin-status-label'
import { HtmlContent } from 'components/atoms/editor/html-content'
import { SearchFilter } from 'components/search-filter'
import { Select } from 'components/atoms/select'
import { SkillLevelOutcomesSection } from 'components/skill-level-outcomes-section'
import { store } from 'store/index'
import * as SecondaryNav from 'components/secondary-nav'
import * as Table from 'components/table'
import { CheckinSkill } from 'store/modules/checkin-skills'
import { fuzzyFilter } from 'app/packs/src/utils/table-helpers'
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table'

type CheckinResult = {
  requiredLevel: number
  status: CheckinStatus
  statusLabel?: string | null
  statusColor?: string | null
}

type SkillRow = {
  skillId: string
  skillName: string
  skillImageUrl?: string
  results: Record<string, CheckinResult | null>
}

type CheckinColumnInfo = {
  id: string
  finalisedAt: Date
  positionName: string
}

type HistoryEntry = {
  id: string
  avatarUrl?: string
  name: string
  initials: string
  positionName: string
  status: CheckinStatus
  statusLabel?: string | null
  statusColor?: string | null
  notes: string
  date: Date
}

type SelectedSkillData = {
  skillId: string
  skillName: string
  skillDescription?: string
  requiredLevel: number
  status: CheckinStatus
  statusLabel?: string | null
  statusColor?: string | null
  outcomes: Array<{ id: string; content: string }>
  history: HistoryEntry[]
}

const columnHelper = createColumnHelper<SkillRow>()

type ArchivedCheckinDetailsPageProps = {
  userId: string
}

export const ArchivedCheckinDetailsPage = observer(
  (props: ArchivedCheckinDetailsPageProps) => {
    const { userId } = props
    const [loading, setLoading] = React.useState(true)
    const [selectedSkillId, setSelectedSkillId] = React.useState<string | null>(
      null
    )
    const handleCloseSidebar = React.useCallback(
      () => setSelectedSkillId(null),
      []
    )

    React.useEffect(() => {
      const fetchData = async () => {
        await Promise.all([
          store.users.fetchOne(userId, { include: ['position'] }),
          store.checkins.fetchAll({
            filter: { archived: true, author_id: userId },
            include: [
              'assessor',
              'author',
              'checkin_skills',
              'checkin_skills.skill',
            ],
            page: { size: 9999 },
          }),
          store.userSkills.fetchForUser(userId),
        ])
        setLoading(false)
      }
      fetchData()
    }, [userId])

    const user = store.users.byId(userId)

    const archivedCheckins = React.useMemo(() => {
      if (loading) return []
      return store.checkins
        .filtered((c) => c.isArchived && c.author.id === userId)
        .sort((a, b) => {
          if (a.finalisedAt && b.finalisedAt)
            return a.finalisedAt > b.finalisedAt ? -1 : 1
          return 0
        })
    }, [loading, userId, store.checkins.all.length]) // eslint-disable-line react-hooks/exhaustive-deps

    const checkinColumns: CheckinColumnInfo[] = React.useMemo(() => {
      return archivedCheckins.map((checkin) => ({
        id: checkin.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        finalisedAt: checkin.finalisedAt!,
        positionName: checkin.positionName || '',
      }))
    }, [archivedCheckins])

    const rows: SkillRow[] = React.useMemo(() => {
      const skillMap = new Map<
        string,
        {
          skillId: string
          skillName: string
          skillImageUrl?: string
          results: Record<string, CheckinResult | null>
        }
      >()

      for (const checkin of archivedCheckins) {
        const checkinSkills: CheckinSkill[] = checkin.checkinSkills || []

        for (const cs of checkinSkills) {
          const skillId = cs.skill?.id || cs.associatedData?.skill?.id
          const skillName =
            cs.skill?.name || cs.associatedData?.skill?.name || 'Unknown'
          const skillImageUrl = cs.skill?.imageUrl || undefined

          if (!skillId) continue

          if (!skillMap.has(skillId)) {
            skillMap.set(skillId, {
              skillId,
              skillName,
              skillImageUrl,
              results: {},
            })
          }

          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const row = skillMap.get(skillId)!
          if (!cs.initiallySkipped) {
            row.results[checkin.id] = {
              requiredLevel: cs.requiredLevel,
              status: cs.status,
              statusLabel: cs.statusLabel,
              statusColor: cs.statusColor,
            }
          } else {
            row.results[checkin.id] = null
          }
        }

        for (const [, row] of skillMap) {
          if (!(checkin.id in row.results)) {
            row.results[checkin.id] = null
          }
        }
      }

      return Array.from(skillMap.values()).sort((a, b) =>
        a.skillName.localeCompare(b.skillName)
      )
    }, [archivedCheckins])

    const [globalFilter, setGlobalFilter] = React.useState('')
    const [positionFilter, setPositionFilter] = React.useState<string[]>([])

    const positionOptions = React.useMemo(() => {
      const positions = new Set<string>()
      for (const col of checkinColumns) {
        if (col.positionName) positions.add(col.positionName)
      }
      return Array.from(positions).map((name) => ({
        label: name,
        value: name,
      }))
    }, [checkinColumns])

    const visibleCheckinColumns = React.useMemo(() => {
      if (positionFilter.length === 0) return checkinColumns
      return checkinColumns.filter((col) =>
        positionFilter.includes(col.positionName)
      )
    }, [checkinColumns, positionFilter])

    const filteredRows: SkillRow[] = React.useMemo(() => {
      if (positionFilter.length === 0) return rows
      return rows.filter((row) =>
        visibleCheckinColumns.some((col) => row.results[col.id] !== null)
      )
    }, [rows, positionFilter, visibleCheckinColumns])

    const columns = React.useMemo(() => {
      const nameCol = columnHelper.accessor('skillName', {
        header: 'Name',
        cell: (data) => (
          <SkillNameCell
            row={data.row.original}
            onClick={() => setSelectedSkillId(data.row.original.skillId)}
          />
        ),
        id: 'name',
      })

      const resultCols = visibleCheckinColumns.map((col) =>
        columnHelper.display({
          id: `checkin-${col.id}`,
          header: () => <CheckinColumnHeader info={col} />,
          cell: (data) => {
            const result = data.row.original.results[col.id]
            if (!result) return <NaTag />
            return <ResultCell result={result} />
          },
        })
      )

      return [nameCol, ...resultCols] as ColumnDef<SkillRow, unknown>[]
    }, [visibleCheckinColumns])

    const table = useReactTable({
      data: filteredRows,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      globalFilterFn: fuzzyFilter,
      state: {
        globalFilter,
      },
      onGlobalFilterChange: setGlobalFilter,
      filterFns: {
        fuzzyFilter,
      },
    })

    const selectedSkillData: SelectedSkillData | null = React.useMemo(() => {
      if (!selectedSkillId) return null

      let skillName = ''
      let skillDescription: string | undefined
      let requiredLevel = 0
      let status: CheckinStatus = null
      let statusLabel: string | null = null
      let statusColor: string | null = null
      let outcomes: Array<{ id: string; content: string }> = []
      const history: HistoryEntry[] = []

      for (const checkin of archivedCheckins) {
        const cs = checkin.checkinSkills?.find(
          (s) =>
            s.skill?.id === selectedSkillId ||
            s.associatedData?.skill?.id === selectedSkillId
        )
        if (!cs || cs.initiallySkipped) continue

        if (!skillName) {
          skillName = cs.skill?.name || cs.associatedData?.skill?.name || ''
          skillDescription =
            cs.skill?.description ||
            cs.associatedData?.skill?.description ||
            undefined
          requiredLevel = cs.requiredLevel
          status = cs.status
          statusLabel = cs.statusLabel
          statusColor = cs.statusColor

          const levelOutcomes = cs.associatedSkillLevelOutcomes || []
          outcomes = levelOutcomes.map((o) => ({
            id: o.id,
            content: o.content,
          }))
        }

        const positionName = checkin.positionName || ''
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const date = checkin.finalisedAt!

        if (cs.description) {
          const author = checkin.author
          const [fname, lname] = (author?.fullName || '').split(' ')
          history.push({
            id: `${checkin.id}-author`,
            avatarUrl: author?.avatarUrl || undefined,
            name: author?.fullName || 'Unknown',
            initials: (fname?.[0] || '') + (lname?.[0] || ''),
            positionName,
            status: cs.status,
            statusLabel: cs.statusLabel,
            statusColor: cs.statusColor,
            notes: cs.description,
            date,
          })
        }

        if (cs.assessorDescription && checkin.assessor) {
          const assessor = checkin.assessor
          const [fname, lname] = (assessor?.fullName || '').split(' ')
          history.push({
            id: `${checkin.id}-assessor`,
            avatarUrl: assessor?.avatarUrl || undefined,
            name: assessor?.fullName || 'Unknown',
            initials: (fname?.[0] || '') + (lname?.[0] || ''),
            positionName,
            status: cs.status,
            statusLabel: cs.statusLabel,
            statusColor: cs.statusColor,
            notes: cs.assessorDescription,
            date,
          })
        }

        if (cs.finalDescription) {
          const reviewer = checkin.assessor || checkin.author
          const [fname, lname] = (reviewer?.fullName || '').split(' ')
          history.push({
            id: `${checkin.id}-final`,
            avatarUrl: reviewer?.avatarUrl || undefined,
            name: reviewer?.fullName || 'Unknown',
            initials: (fname?.[0] || '') + (lname?.[0] || ''),
            positionName,
            status: cs.status,
            statusLabel: cs.statusLabel,
            statusColor: cs.statusColor,
            notes: cs.finalDescription,
            date,
          })
        }
      }

      if (!skillName) return null

      return {
        skillId: selectedSkillId,
        skillName,
        skillDescription,
        requiredLevel,
        status,
        statusLabel,
        statusColor,
        outcomes,
        history,
      }
    }, [selectedSkillId, archivedCheckins])

    return (
      <>
        <SecondaryNav.Root>
          <SecondaryNav.Header className="font-talent !font-semibold !text-base leading-5">
            Archived Check-ins for {user?.fullName || 'User'}
          </SecondaryNav.Header>
        </SecondaryNav.Root>
        <main className="pt-4 lg:pt-10 overflow-x-auto flex-1 mb-10">
          <div className="flex gap-x-3 mb-5 px-4">
            <SearchFilter
              name="archived-checkin-details-search"
              onChange={(value) => setGlobalFilter(String(value))}
              value={globalFilter ?? ''}
            />
            <Select
              isMulti
              isSearchable
              onChange={(values: string[]) => setPositionFilter(values)}
              options={positionOptions}
              triggerContent={<FilterTriggerContent />}
              value={positionFilter}
            />
          </div>
          <div className="overflow-x-auto pb-10">
            <Table.Root>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.Header
                        key={header.id}
                        header={header}
                        source="archived-checkin-details-table"
                        loading={loading}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Table.Header>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>
              <Table.Body>
                {table.getRowModel().rows.length === 0 && (
                  <Table.NoResultsRow colspan={table.getAllColumns().length} />
                )}
                {table.getRowModel().rows.map((row, index) => (
                  <Table.Row
                    showBottomBorder={
                      index < table.getRowModel().rows.length - 1
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell key={cell.id} className="whitespace-nowrap">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </div>
        </main>

        <AnimatePresence>
          {selectedSkillData && (
            <SkillDetailSidebar
              data={selectedSkillData}
              userId={userId}
              onClose={handleCloseSidebar}
            />
          )}
        </AnimatePresence>
      </>
    )
  }
)

function SkillNameCell({
  row,
  onClick,
}: {
  row: SkillRow
  onClick: () => void
}) {
  return (
    <button
      className="flex gap-x-2 items-center bg-transparent border-0 p-0 cursor-pointer text-left"
      onClick={onClick}
    >
      <div
        className="border border-white bg-image-cover flex-shrink-0 h-8 rounded w-8"
        style={{
          backgroundImage: row.skillImageUrl
            ? `url("${encodeURI(row.skillImageUrl)}")`
            : 'none',
        }}
      />
      <span className="font-bold m-0 w-max text-sm">{row.skillName}</span>
    </button>
  )
}

function SkillDetailSidebar({
  data,
  userId,
  onClose,
}: {
  data: SelectedSkillData
  userId: string
  onClose: () => void
}) {
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [onClose])

  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/30 z-30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="fixed inset-y-0 right-0 z-40 bg-white border-0 border-l border-solid border-gray-100 flex flex-col"
        style={{ width: '500px', maxWidth: '100vw' }}
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-0 border-b border-solid border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <button
              aria-label="Close sidebar"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 bg-transparent border-0 cursor-pointer flex-shrink-0"
            >
              <CaretLeft className="h-5 w-5 text-gray-500" weight="bold" />
            </button>
            <h2 className="font-talent text-[19px] leading-6 font-medium text-[#1D264A] m-0 truncate">
              {data.skillName}
            </h2>
            <LevelTag level={data.requiredLevel} />
            <CheckinStatusLabel
              status={data.status}
              statusLabel={data.statusLabel}
              statusColor={data.statusColor}
            />
            <FocusSkillStar
              editable={userId === store.currentUser?.id}
              size={16}
              source="archived-checkin-details-sidebar"
              userSkill={store.userSkills.forSkillAndUser(data.skillId, userId)}
            />
          </div>
          <button
            aria-label="Close sidebar"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full border-0 cursor-pointer flex-shrink-0"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {data.skillDescription && (
            <div className="mb-5">
              <HtmlContent className="text-sm text-gray-700">
                {data.skillDescription}
              </HtmlContent>
            </div>
          )}

          {data.outcomes.length > 0 && (
            <div className="mb-6">
              <SkillLevelOutcomesSection outcomes={data.outcomes} />
            </div>
          )}

          {data.history.length > 0 && (
            <div>
              <h3 className="font-talent font-semibold text-base leading-5 text-[#1D264A] mb-4">
                History
              </h3>
              <div className="flex flex-col gap-5">
                {data.history.map((entry) => (
                  <HistoryEntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  )
}

function HistoryEntryCard({ entry }: { entry: HistoryEntry }) {
  const dateStr = new Date(entry.date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="flex gap-3">
      <Avatar
        size="sm"
        className="flex-shrink-0 mt-0.5"
        fallbackText={entry.initials}
        src={entry.avatarUrl}
        alt={`Avatar for ${entry.name}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-talent font-normal text-base leading-5 text-gray-900 truncate">
              {entry.name}
            </span>
            <span className="font-talent font-normal text-[15px] leading-[18px] text-gray-500 truncate">
              {entry.positionName}
            </span>
          </div>
          <div className="flex-shrink-0">
            <CheckinStatusLabel
              status={entry.status}
              statusLabel={entry.statusLabel}
              statusColor={entry.statusColor}
            />
          </div>
        </div>
        <div className="font-talent font-normal text-[16px] leading-[20px] text-[#1D264A] mb-1">
          <HtmlContent>{entry.notes}</HtmlContent>
        </div>
        <span className="font-talent font-normal text-[15px] leading-[18px] text-[#5E6580] mt-1">
          {dateStr}
        </span>
      </div>
    </div>
  )
}

function CheckinColumnHeader({ info }: { info: CheckinColumnInfo }) {
  const dateStr = info.finalisedAt
    ? new Date(info.finalisedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : ''

  return (
    <div className="flex flex-col">
      <span className="font-bold text-sm text-gray-900">{dateStr}</span>
      <span className="font-normal text-xs text-gray-500">
        {info.positionName}
      </span>
    </div>
  )
}

function ResultCell({ result }: { result: CheckinResult }) {
  return (
    <div className="flex gap-2 items-center">
      <LevelTag level={result.requiredLevel} />
      <CheckinStatusLabel
        status={result.status}
        statusLabel={result.statusLabel}
        statusColor={result.statusColor}
      />
    </div>
  )
}

function LevelTag({ level }: { level: number }) {
  return (
    <div className="bg-gray-50 border border-gray-100 border-solid rounded flex h-6 items-center justify-center px-1.5">
      <p className="mb-0 text-gray-600 text-xs">L{level}</p>
    </div>
  )
}

function NaTag() {
  return (
    <div
      className="rounded flex h-6 items-center justify-center px-2"
      style={{ backgroundColor: '#E7E4E4' }}
    >
      <p className="mb-0 text-gray-600 text-xs">N/A</p>
    </div>
  )
}

const FilterTriggerContent = () => (
  <div className="flex gap-1 items-center">
    <Funnel aria-hidden className="h-4 text-gray-600 w-4" weight="bold" />
    Filter
  </div>
)
